type ApiListener = (...args: any[]) => void

interface ListenerApi {
  on(event: string, listener: ApiListener): void
  off(event: string, listener: ApiListener): void
}

/** Keeps module listeners attached to one concrete WebSocket API at a time. */
export class ApiListenerRegistry<TApi extends object> {
  private api: TApi | null = null
  private listeners = new Map<string, Set<ApiListener>>()

  isBoundTo(api: TApi): boolean {
    return this.api === api
  }

  get listenerCount(): number {
    let count = 0
    this.listeners.forEach((listeners) => {
      count += listeners.size
    })
    return count
  }

  bind(api: TApi): boolean {
    if (this.api === api) return false
    this.clear()
    this.api = api
    return true
  }

  add(event: string, listener: ApiListener): void {
    if (!this.api) throw new Error('An API must be bound before adding listeners')
    const api = this.api as unknown as ListenerApi
    api.on(event, listener)
    const listeners = this.listeners.get(event) ?? new Set<ApiListener>()
    listeners.add(listener)
    this.listeners.set(event, listeners)
  }

  clear(): void {
    if (this.api) {
      const api = this.api as unknown as ListenerApi
      this.listeners.forEach((listeners, event) => {
        listeners.forEach((listener) => api.off(event, listener))
      })
    }
    this.listeners.clear()
    this.api = null
  }
}
